import { useDroppable } from "@dnd-kit/core";
import styled from "styled-components";

interface DayColumnProps {
  children: React.ReactNode;
  day: number;
  dayName: string;
  dateLabel?: string;
  isVertical?: boolean;
  weekNumber: number;
  isBeforeCurrent?: boolean;
  isCurrent?: boolean;
}

export const DayColumn: React.FC<DayColumnProps> = ({
  children,
  day,
  dayName,
  dateLabel,
  isVertical,
  weekNumber,
  isBeforeCurrent,
  isCurrent,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `week-${weekNumber}-day-${day}`,
    data: {
      droppableType: "protocol_timeline_day",
      weekNumber,
      day,
    },
  });

  return (
    <DayColumnContainer ref={setNodeRef} $isOver={isOver} $isVertical={isVertical} $isCurrent={isCurrent}>
      <DayHeader $isOver={isOver} $isCurrent={isCurrent}>
        <DayName>{dayName}</DayName>
        {dateLabel && <DayDate>{dateLabel}</DayDate>}
      </DayHeader>
      <DayContent $isDisabled={isBeforeCurrent || isCurrent}>{children}</DayContent>
    </DayColumnContainer>
  );
};

const DayColumnContainer = styled.div<{
  $isOver: boolean;
  $isVertical?: boolean;
  $isCurrent?: boolean;
}>`
  background-color: ${({ $isOver }) => ($isOver ? "#e6f2ff" : "#ffffff")};
  box-shadow: ${({ $isOver }) => ($isOver ? "inset 0 0 0 2px #5096E8" : "none")};
  min-height: 200px;
  min-width: 320px;
  width: 320px;
  flex: ${({ $isVertical }) => ($isVertical ? "0 0 auto" : "0 0 320px")};
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s ease;
  overflow: visible;

  ${({ $isCurrent }) =>
    $isCurrent &&
    `
    background-color: #f8fafc;
  `}
`;

const DayHeader = styled.div<{ $isOver: boolean; $isCurrent?: boolean }>`
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #1a2332;
  text-align: center;
  border-bottom: 1px solid ${({ $isCurrent }) => ($isCurrent ? "#0066cc" : "#e8ecf1")};
  background-color: ${({ $isCurrent }) => ($isCurrent ? "#e3f2fd" : "#f8f9fa")};
  box-shadow: ${({ $isOver }) => ($isOver ? "inset 0 0 0 2px #5096E8" : "none")};
`;

const DayName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #5c6f82;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DayDate = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1a2332;
`;

const DayContent = styled.div<{ $isDisabled?: boolean }>`
  padding: 12px 16px;
  flex: 1;
  overflow: visible;
  display: flex;
  flex-direction: column;

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    opacity: 0.6;
  `}
`;
